import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { useSuspenseGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import {
  EmailEventItem,
  emailEventListQuery,
} from "@/app/EmailEvents/GraphQL/emailEventListQuery";
import { formatDateTimeLocal } from "@/lib/Formatters/formatDateTime";
import { cn } from "@/lib/utils";
import {
  AlertTriangleIcon,
  CheckCircle2Icon,
  ClockIcon,
  MailIcon,
  MailOpenIcon,
  MousePointerClickIcon,
  XCircleIcon,
} from "lucide-react";
import { ModelType } from "@/gql/graphql";
import { useTranslations } from "use-intl";
import { isFailedEmailStatus } from "@/app/EmailEvents/Utils/emailEventFailure";
import { getObjectEmailEventsVariables } from "@/app/EmailEvents/Utils/getObjectEmailEventsVariables";

const statusConfig = {
  ACCEPTED: {
    icon: ClockIcon,
    color: "text-gray-400",
    bgColor: "bg-gray-100",
  },
  DELIVERED: {
    icon: CheckCircle2Icon,
    color: "text-green-600",
    bgColor: "bg-green-100",
  },
  OPENED: {
    icon: MailOpenIcon,
    color: "text-blue-600",
    bgColor: "bg-blue-100",
  },
  CLICKED: {
    icon: MousePointerClickIcon,
    color: "text-purple-600",
    bgColor: "bg-purple-100",
  },
  UNSUBSCRIBED: {
    icon: XCircleIcon,
    color: "text-orange-600",
    bgColor: "bg-orange-100",
  },
  STORED: {
    icon: MailIcon,
    color: "text-gray-400",
    bgColor: "bg-gray-100",
  },
  REJECTED: {
    icon: XCircleIcon,
    color: "text-red-600",
    bgColor: "bg-red-100",
  },
  COMPLAINED: {
    icon: AlertTriangleIcon,
    color: "text-red-600",
    bgColor: "bg-red-100",
  },
  PERMANENT_FAIL: {
    icon: XCircleIcon,
    color: "text-red-600",
    bgColor: "bg-red-100",
  },
  TEMPORARY_FAIL: {
    icon: AlertTriangleIcon,
    color: "text-yellow-600",
    bgColor: "bg-yellow-100",
  },
};

type EmailSend = {
  messageId: string;
  type: EmailEventItem["type"];
  createdAt: string;
  recipients: EmailEventItem[];
};

/**
 * One message produces one row per envelope recipient (To + BCC admin), so
 * group by message_id to show a single send with its per-recipient outcome.
 */
function groupByMessage(events: readonly EmailEventItem[]): EmailSend[] {
  const sends = new Map<string, EmailSend>();

  events.forEach((event) => {
    const send = sends.get(event.message_id);

    if (send) {
      send.recipients.push(event);
      return;
    }

    sends.set(event.message_id, {
      messageId: event.message_id,
      type: event.type,
      createdAt: event.created_at,
      recipients: [event],
    });
  });

  return [...sends.values()];
}

export function EmailTimelineDialog({
  objectType,
  objectId,
}: {
  objectType: ModelType;
  objectId: string;
}) {
  const t = useTranslations("emailEvent");
  const { data } = useSuspenseGqlQuery(
    emailEventListQuery,
    getObjectEmailEventsVariables(objectType, objectId),
  );

  const sends = groupByMessage(data?.emailEvents.data ?? []);

  return (
    <BaseDialog title="Email Timeline" size="2xl">
      {sends.length === 0 ? (
        <div className="py-8 text-center text-sm text-gray-500">
          No email events found
        </div>
      ) : (
        <ul className="divide-y divide-gray-200 overflow-hidden rounded-lg border border-gray-200">
          {sends.map((send) => {
            const hasFailure = send.recipients.some((recipient) =>
              isFailedEmailStatus(recipient.status),
            );

            return (
              <li key={send.messageId} className="px-4 py-3">
                <div className="flex items-baseline justify-between gap-4">
                  <span
                    className={cn(
                      "text-sm font-medium",
                      hasFailure ? "text-red-700" : "text-gray-900",
                    )}
                  >
                    {t(`type.${send.type}`)}
                  </span>
                  <span className="whitespace-nowrap text-xs text-gray-500">
                    {formatDateTimeLocal(send.createdAt)}
                  </span>
                </div>
                <ul className="mt-2 flex flex-col gap-1.5">
                  {send.recipients.map((recipient) => {
                    const config = statusConfig[recipient.status];
                    const Icon = config.icon;

                    return (
                      <li key={recipient.id} className="text-sm">
                        <div className="flex items-center gap-2">
                          <Icon
                            className={cn("size-4 shrink-0", config.color)}
                            aria-hidden="true"
                          />
                          <span
                            className={cn(
                              "inline-flex shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium",
                              config.bgColor,
                              config.color,
                            )}
                          >
                            {t(`status.${recipient.status}`)}
                          </span>
                          <span className="truncate text-gray-600">
                            {recipient.recipient ? recipient.recipient : "—"}
                          </span>
                        </div>
                        {recipient.failure_reason ? (
                          <p className="ml-6 mt-0.5 text-xs text-red-600">
                            {recipient.failure_code
                              ? `${recipient.failure_code} `
                              : null}
                            {recipient.failure_reason}
                          </p>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </li>
            );
          })}
        </ul>
      )}
    </BaseDialog>
  );
}
